import _ from 'lodash';

import AdminNotification from 'models/notification/admin_notification';
import AgentActivity from 'models/agent_activity';
import AgentSubscriptions from './lib/agent_subscriptions';
import { AnswersSortType } from 'models/answer_panel';
import Boot from 'models/location/boot';
import createBrowserCapabilities from 'scripts/domain/factories/configuration/create_browser_capabilities';
import FetchEmailSuggestions from 'actions/email_suggestions/fetch_email_suggestions';
import FetchGreetingSuggestions from 'actions/greeting_suggestions/fetch_greeting_suggestions';
import GatewayErrorSilentHandler from 'scripts/application/lib/gateway_error_silent_handler';
import mixin from 'scripts/lib/mixin';
import RequestOrganization from 'actions/organization/request_organization';
import { setAgentBusy } from 'actions/agent_status/lib/agent_status';
import SetAndLogActivity from 'actions/agent_activity/set_and_log_activity';
import UserActivation from 'models/location/user_activation';
import UserLogin from 'models/location/user_login';
import UserResetPassword from 'models/location/user_reset_password';

export default class FeatureSetGatewayObserver {
  constructor(context) {
    this.context = context;
    this.agentSubscriptions = new AgentSubscriptions(this.context);
  }

  updateAppFeatures(dto) {
    let store = this.context.stores.appFeatures;
    let currentAppFeatures = store.get();

    let newGatekeeperFeatures = _.clone(dto);
    let newAppFeatures = currentAppFeatures.replace('gatekeeperFeatures', newGatekeeperFeatures);
    newAppFeatures = newAppFeatures.replace('browserCapabilities', {
      ...currentAppFeatures.browserCapabilities,
      ...createBrowserCapabilities(),
    });
    store.set(newAppFeatures);

    this.handleDemoModeChange(currentAppFeatures, newAppFeatures);
    this.handleWebRtcChange(currentAppFeatures, newAppFeatures);
    this.handleAnswerSearchBestMatch(currentAppFeatures, newAppFeatures);
    this.handleAnalytics(newAppFeatures);
    this.handleGladlyOnGladlyChat(newAppFeatures);

    if (currentAppFeatures.gatekeeperFeatures) {
      // it's an update if gatekeeper features already exist
      this.context.router.refresh();
      return;
    }

    let auth = this.context.stores.auth.get();
    if (!auth.isUserActivated()) {
      this.continueToUserActivation();
      return;
    }

    if (auth.isPasswordBeingReset()) {
      this.continueToPasswordReset();
      return;
    }

    this.completeStartup();
  }

  handleDemoModeChange(currentAppFeatures, newAppFeatures) {
    if (newAppFeatures.isEnabled('demoMode') && !currentAppFeatures.isEnabled('demoMode')) {
      if (currentAppFeatures.gatekeeperFeatures) {
        // it's an update if gatekeeper features already exist
        this.notifyAgent('Reload your window to enter demo mode (press CTRL+R or \u2318+R)');
      } else {
        this.context.backends.enableDemoMode();
      }
    } else if (!newAppFeatures.isEnabled('demoMode') && currentAppFeatures.isEnabled('demoMode')) {
      this.notifyAgent('Reload your window to exit demo mode (press CTRL+R or \u2318+R)');
    }
  }

  handleWebRtcChange(currentAppFeatures, newAppFeatures) {
    if (!this.isInternalAgentActionsEnabled()) {
      return;
    }

    if (newAppFeatures.isEnabled('webRTC') && !currentAppFeatures.isEnabled('webRTC')) {
      this.context.gateways.deviceStatus.getStatus();
    } else if (!newAppFeatures.isEnabled('webRTC') && currentAppFeatures.isEnabled('webRTC')) {
      this.context.gateways.phoneGateway.deregisterAgent(this.getUserId());
    }
  }

  handleAnalytics(newAppFeatures) {
    if (newAppFeatures.isEnabled('disableAnalytics')) {
      this.context.analytics.reset();
    } else {
      this.context.analytics.start();
    }
  }

  handleGladlyOnGladlyChat(newAppFeatures) {
    let roles = this.context.stores.auth.get().getRoleIds() || [];
    if (newAppFeatures.isEnabled('gladlyOnGladlyChat') && _.includes(roles, 'system-admin')) {
      this.context.chat.start();
    }
  }

  handleAnswerSearchBestMatch(oldAppFeatures, newAppFeatures) {
    const isEnabled = newAppFeatures.isEnabled('answerSearchBestMatch');
    if (isEnabled !== oldAppFeatures.isEnabled('answerSearchBestMatch')) {
      const sortType = isEnabled ? AnswersSortType.RELEVANCE : AnswersSortType.FREQUENCY;
      const answerPanel = this.context.stores.answerPanel.get();
      answerPanel.setSortType(sortType);
      this.context.stores.answerPanel.set(answerPanel);
    }
  }

  notifyAgent(message) {
    this.context.stores.notifications.add(AdminNotification.create({ message }));
  }

  continueToPasswordReset() {
    this.context.router.navigateTo(UserResetPassword.create());
  }

  continueToUserActivation() {
    this.context.router.navigateTo(UserActivation.create());
  }

  isImpersonatedUser() {
    let auth = this.context.stores.auth.get();
    return auth && !!auth.claims.saganUser;
  }

  completeStartup() {
    this.subscribeToModels();
    let currentLocation = this.context.stores.currentLocation.get();
    if (currentLocation instanceof UserLogin || currentLocation.isFromSso || this.isImpersonatedUser()) {
      this.completeLogin();
    }

    if (currentLocation instanceof UserLogin) {
      this.navigateToPreAuthPath(currentLocation);
    } else if (currentLocation instanceof Boot) {
      this.completeBoot(currentLocation);
    }

    this.context.capacityManager.start();
  }

  subscribeToModels() {
    this.subscribeToOrgModels();
    if (this.isInternalAgentActionsEnabled()) {
      this.agentSubscriptions.subscribe(this.getUserId());
    }
  }

  // Org models are used throughout the app (i.e. not tied to a specific view or
  // customer, only the current organization).
  subscribeToOrgModels() {
    this.context.gateways.agent.request();
    this.context.stores.agents.setLoading();

    this.context.gateways.agentStatusReason.requestAll();

    this.context.gateways.channelConfiguration.request();
    this.context.gateways.communicationQueue.requestAll();
    this.context.gateways.companyProfile.request();

    // fetch initial language list; updated when toggling answer panel
    this.context.gateways.language.fetch();
    this.context.stores.languages.setLoading();

    this.context.gateways.routingGroup.request();
    this.context.stores.routingGroups.setLoading();

    this.context.gateways.stationConfiguration.request();
    this.context.stores.stationConfigurations.setLoading();
    this.context.gateways.topic.request();

    if (this.isInternalAgentActionsEnabled()) {
      this.context.gateways.agentAssistanceConfig.request();

      this.context.gateways.messagingConfiguration.request();
      this.context.stores.messagingConfiguration.setLoading();

      this.context.gateways.conversationWorkflowConfig.request();
      this.context.stores.conversationWorkflowConfig.setLoading();

      this.context.gateways.customerProfileDef.request();
      this.context.stores.customerProfileDef.setLoading();

      this.context.gateways.externalCustomerLookupStatus.request();
      this.context.stores.externalCustomerLookupStatus.setLoading();

      this.context.gateways.relationshipLabel.request();
      this.context.stores.relationshipLabels.setLoading();

      this.context.gateways.routingQueueItem.request();
      this.context.gateways.systemConfiguration.request();

      this.context.gateways.team.request();
      this.context.stores.teams.setLoading();

      this.context.gateways.voiceConfiguration.request();
    }

    if (this.isAnswersAdminEnabled() || this.isInternalAgentActionsEnabled()) {
      this.context.gateways.kbVariable.request();
    }

    // fetch topic hierarchy
    this.context.gateways.topicHierarchy.requestAll();
    this.context.stores.topicHierarchy.setLoading();

    this.context.executeAction(RequestOrganization);

    if (this.isExternalAgentActionsEnabled()) {
      // phrase suggestions are pre-fetched
      // they're a static list by org so not subscribed to
      this.context.gateways.phraseSuggestions.fetch();
      this.context.stores.phraseSuggestions.setLoading();
      this.context.gateways.vgsAccount.fetchAll();
      this.context.executeAction(FetchEmailSuggestions);
      this.context.executeAction(FetchGreetingSuggestions);
    }
  }

  completeLogin() {
    this.context.executeAction(SetAndLogActivity, { agentId: this.getUserId(), type: AgentActivity.Type.LOG_IN });
    setAgentBusy(this.context, this);
  }

  navigateToPreAuthPath(userLogin) {
    if (userLogin.preAuthPath) {
      if (userLogin.preAuthPath.external) {
        this.context.router.navigateToPath(userLogin.preAuthPath.path);
      } else {
        this.context.router.navigateToUrl(userLogin.preAuthPath.path);
      }
    } else {
      this.context.router.navigateHome();
    }
  }

  completeBoot(boot) {
    boot.complete();
    this.context.stores.currentLocation.set(boot);

    if (boot.requestedPath) {
      this.context.router.navigateToUrl(boot.requestedPath);
    } else {
      this.context.router.navigateHome();
    }
  }

  getUserId() {
    return this.context.stores.auth.get().getUserId();
  }

  handleFeatureSet(dto) {
    this.updateAppFeatures(dto);
  }

  handleFetchSuccess(ctx, dto) {
    this.updateAppFeatures(dto);
  }

  handleFetchError(ctx, err) {
    this.handleCommonErrors(ctx, err);
  }

  isInternalAgentActionsEnabled() {
    return this.context.stores.appFeatures.get().isEnabled('internalAgentActions');
  }

  isExternalAgentActionsEnabled() {
    return this.context.stores.appFeatures.get().isEnabled('externalAgentActions');
  }

  isAnswersAdminEnabled() {
    return this.context.stores.appFeatures.get().isEnabled('kbManagement');
  }
}

mixin(FeatureSetGatewayObserver.prototype, GatewayErrorSilentHandler.prototype);



// WEBPACK FOOTER //
// ./src/scripts/application/actions/configuration/feature_set_gateway_observer.js